import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, Phone, Mail, MapPin, ImageIcon, ArrowLeft } from "lucide-react";
import { API, BACKEND_URL } from "../lib/api";

const STATUSES = [
  { value: "nouveau", label: "Nouveau", cls: "bg-sky-100 text-sky-700" },
  { value: "contacte", label: "Contacté", cls: "bg-amber-100 text-amber-700" },
  { value: "devis_envoye", label: "Devis envoyé", cls: "bg-violet-100 text-violet-700" },
  { value: "termine", label: "Terminé", cls: "bg-emerald-100 text-emerald-700" },
];

const statusInfo = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200";

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`);
      setQuotes(data);
    } catch {
      toast.error("Impossible de charger les demandes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadQuotes();
  }, [user, loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setUser(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400 text-sm">Chargement…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-sky-50/60 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl bg-white border border-slate-100 shadow-xl shadow-sky-900/10 p-8 sm:p-10">
          <div className="flex flex-col items-center gap-2">
            <img src="/logo.png" alt="WM Clean" className="h-10 w-auto" />
            <span className="text-slate-400 font-semibold text-sm">Espace pro</span>
          </div>
          <form onSubmit={login} data-testid="admin-login-form" className="mt-8 space-y-4">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
              <input id="admin-email" data-testid="admin-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@wmclean.fr" />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Mot de passe</label>
              <input id="admin-password" data-testid="admin-login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2.5">{error}</p>
            )}
            <button type="submit" data-testid="admin-login-submit-button"
              className="w-full rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-6 py-3.5 text-sm shadow-lg shadow-sky-600/25 transition-colors">
              Se connecter
            </button>
          </form>
          <a href="/" data-testid="admin-back-to-site-link" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-sky-600 transition-colors">
            <ArrowLeft size={13} /> Retour au site
          </a>
        </div>
      </div>
    );
  }

  const fresh = quotes.filter((q) => q.status === "nouveau").length;

  return (
    <div data-testid="admin-leads-dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="WM Clean" className="h-8 w-auto" />
            <span className="font-heading font-extrabold text-lg text-slate-900">Demandes de devis</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-dashboard-site-link" className="hidden sm:inline text-sm text-slate-500 hover:text-sky-600 transition-colors">Voir le site</a>
            <button onClick={loadQuotes} data-testid="admin-refresh-button" aria-label="Rafraîchir"
              className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-sky-600 transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:text-red-600 hover:border-red-200 transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-3 mb-8">
          <span data-testid="admin-stat-total" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
            <Inbox size={15} className="text-sky-600" /> {quotes.length} demande{quotes.length > 1 ? "s" : ""}
          </span>
          <span data-testid="admin-stat-new" className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white">
            {fresh} nouvelle{fresh > 1 ? "s" : ""}
          </span>
        </div>

        {quotes.length === 0 ? (
          <div data-testid="admin-empty-state" className="rounded-3xl bg-white border border-slate-100 p-16 text-center text-slate-400 text-sm">
            Aucune demande pour le moment. Les demandes du formulaire apparaîtront ici.
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q, i) => {
              const s = statusInfo(q.status);
              return (
                <article key={q.id} data-testid={`admin-lead-row-${i + 1}`}
                  className="rounded-3xl bg-white border border-slate-100 p-6 flex flex-col lg:flex-row lg:items-center gap-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-heading font-bold text-slate-900">{q.name}</h3>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${s.cls}`}>{s.label}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(q.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
                      <a href={`tel:${q.phone.replace(/\s/g, "")}`} data-testid={`admin-lead-phone-${i + 1}`} className="inline-flex items-center gap-1.5 hover:text-sky-600">
                        <Phone size={13} className="text-sky-500" /> {q.phone}
                      </a>
                      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-sky-600">
                        <Mail size={13} className="text-sky-500" /> {q.email}
                      </a>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} className="text-sky-500" /> {q.address}
                      </span>
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {q.property_type} · {q.service_type}
                    </p>
                    {q.message && <p className="mt-2 text-sm text-slate-500 leading-relaxed">« {q.message} »</p>}
                    {q.photo_url && (
                      <a href={`${BACKEND_URL}${q.photo_url}`} target="_blank" rel="noreferrer" data-testid={`admin-lead-photo-${i + 1}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:underline">
                        <ImageIcon size={13} /> Voir la photo jointe
                      </a>
                    )}
                  </div>
                  <select
                    value={q.status}
                    onChange={(e) => updateStatus(q.id, e.target.value)}
                    data-testid={`admin-lead-status-select-${i + 1}`}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 lg:w-44"
                  >
                    {STATUSES.map((st) => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
